import { Action } from "redux";
import { ThunkAction } from "redux-thunk";
import queryString from "query-string";
import { endpoints } from "..";
import { IAppState, ApiRequest } from "../../Common";
import { Feedback } from "../Models/Feedback";

import { FeedbackState, FeedbackCategory, FeedbackSortOption } from "../Constants/feedbackEnums";
import { SearchContextType } from "../../Layout/Constants/LayoutEnums";
import { SearchContext } from "../../Layout/Models/SearchContext";
import { Transition } from "../../Workflows/Models/Transition";

// general interface to describe types that contain a "ListId" which is used as the key in the feedbacklists dictionary in the app state
interface IHasListId {
  listId: string;
}

interface IFeedbackListAction extends Action {
  type: FeedbackListActionType;
  payload: any;
}

// ARGUMENT TYPES

// Arguments for the getFeedbacks action creator (which triggers the action FEEDBACK_SEARCH_LOADED when it's done loading, or the FEEDBACK_SEARCH_SET ERROR action if it failed)
export interface IGetFeedbacksArguments extends IHasListId {
  maxNumToGet?: number; // this is the maximum number of feedbacks to get. If not specified will return as many as possible
  category?: FeedbackCategory; // this is the desired category of feedback. If not specified will return all categories
  sort?: FeedbackSortOption; // when not specified will order by recent
  isAscending?: boolean; // when not specified will be false
  searchQuery?: string; // when not specified, no text search will occur
  username?: string;
  shortname?: string;
  context?: SearchContext;
  startDate?: Date;
  endDate?: Date;
  tags?: number[];
  assignedUsers?: number[];
  selectedCategories?: FeedbackCategory[];
  selectedApplications?: number[];
  selectedTopics?: number[];
}

// arguments for the loadTransitionsIntoList action creator
export interface ILoadTransitionsIntoListArguments extends IHasListId {
  feedbackCodes: string[];
}

// Arguments for the unloadSearch action creator
export interface IUnloadSearchArguments extends IHasListId {}

// Arguments for the setLoading action creator
export interface ISetSearchLoadingArguments extends IHasListId {}

// Arguments for the setTransitionsLoading action creator
export interface ISetSearchTransitionsLoadingArguments extends IHasListId {}

// Arguments for the updateListItem action creator
export interface IUpdateListItemArguments extends IHasListId {
  updatedFeedback: Feedback;
}

// Arguments for the setErrored action creator
export interface ISetSearchErroredArguments extends IHasListId {
  errorMessage: string;
  errorCode: number;
}

// Arguments for the setSavedFilters action creator
export interface ISetSearchSavedFiltersArguments extends IHasListId {
  filters: any[];
}

// PAYLOAD TYPES

// Payload for the FEEDBACK_SEARCH_LOADED action, contains the loaded feedbacks
export interface IFeedbackSearchLoadedActionPayload extends IHasListId {
  loadedFeedbacks: Feedback[];
  fullResultsCount: number;
}

// Payload for the FEEDBACK_TRANSITIONS_LOADED action, contains the loaded transitions
export interface IFeedbackTransitionsLoadedActionPayload extends IHasListId {
  loadedTransitions: { [code: string]: Transition[] };
}

// Payload for the FEEDBACK_SEARCH_UNLOAD action
export interface IUnloadSearchActionPayload extends IHasListId {}

// Payload for the FEEDBACK_SEARCH_SET_LOADING action
export interface ISetSearchLoadingActionPayload extends IHasListId {}

// Payload for the FEEDBACK_SEARCH_SET_TRANSITIONS_LOADING action
export interface ISetSearchTransitionsLoadingActionPayload extends IHasListId {}

// Payload for the FEEDBACK_SEARCH_SET_ERRORED action
export interface ISetSearchErroredActionPayload extends IHasListId {
  errorMessage: string;
  errorCode: number;
}

// Payload for the FEEDBACK_UPDATE_LIST_ITEM action
export interface IFeedbackUpdateListItemPayload extends IHasListId {
  updatedFeedback: Feedback;
}

// payload for the FEEDBACK_SEARCH_SET_SAVED_FILTERS action
export interface IFeedbackSearchSetSavedFiltersActionPayload extends IHasListId {
  filters: any[];
}

// ACTION TYPES

// Action for the FEEDBACK_SEARCH_LOADED action
export interface IFeedbackSearchLoadedAction extends IFeedbackListAction {
  payload: IFeedbackSearchLoadedActionPayload;
}

// Action for the FEEDBACK_TRANSITIONS_LOADED action
export interface IFeedbackTransitionsLoadedAction extends IFeedbackListAction {
  payload: IFeedbackTransitionsLoadedActionPayload;
}

// Action for the FEEDBACK_SEARCH_UNLOAD action
export interface IFeedbackSearchUnloadAction extends IFeedbackListAction {
  payload: IUnloadSearchActionPayload;
}

// Action for the FEEDBACK_SEARCH_SET_LOADING action
export interface ISetSearchLoadingAction extends IFeedbackListAction {
  payload: ISetSearchLoadingActionPayload;
}

// Action for the FEEDBACK_SEARCH_SET_TRANSITIONS_LOADING action
export interface ISetSearchTransitionsLoadingAction extends IFeedbackListAction {
  payload: ISetSearchTransitionsLoadingActionPayload;
}

// Action for the FEEDBACK_SEARCH_SET_ERRORED action
export interface ISetSearchErroredAction extends IFeedbackListAction {
  payload: ISetSearchErroredActionPayload;
}

// Action for the FEEDBACK_UPDATE_LIST_ITEM action
export interface IFeedbackUpdateListItemAction extends IFeedbackListAction {
  payload: IFeedbackUpdateListItemPayload;
}

// Action for the FEEDBACK_SEARCH_SET_SAVED_FILTERS action
export interface ISetSearchSavedFiltersAction extends IFeedbackListAction {
  payload: IFeedbackSearchSetSavedFiltersActionPayload;
}

// Union of all action types. If you define a new action type, add it here please
export type FeedbackListAction =
  | IFeedbackSearchLoadedAction
  | IFeedbackTransitionsLoadedAction
  | IFeedbackSearchUnloadAction
  | ISetSearchLoadingAction
  | ISetSearchTransitionsLoadingAction
  | ISetSearchErroredAction
  | IFeedbackUpdateListItemAction
  | ISetSearchSavedFiltersAction;

export enum FeedbackListActionType {
  FEEDBACK_SEARCH_LOADED = "FEEDBACK_SEARCH_LOADED",
  FEEDBACK_SEARCH_TRANSITIONS_LOADED = "FEEDBACK_SEARCH_TRANSITIONS_LOADED",
  FEEDBACK_SEARCH_UNLOAD = "FEEDBACK_SEARCH_UNLOAD",
  FEEDBACK_SEARCH_SET_LOADING = "FEEDBACK_SEARCH_SET_LOADING",
  FEEDBACK_SEARCH_SET_TRANSITIONS_LOADING = "FEEDBACK_SEARCH_SET_TRANSITIONS_LOADING",
  FEEDBACK_SEARCH_SET_ERRORED = "FEEDBACK_SEARCH_SET_ERRORED",
  FEEDBACK_UPDATE_LIST_ITEM = "FEEDBACK_UPDATE_LIST_ITEM",
  FEEDBACK_SEARCH_SET_SAVED_FILTERS = "FEEDBACK_SEARCH_SET_SAVED_FILTERS"
}

// helper method for building the search URL
function assembleGetFeedbacksUrl(args: IGetFeedbacksArguments): string {
  let url: string = endpoints.feedback.search();

  const queryArgs = {
    maxNumToGet: args.maxNumToGet,
    category: args.category,
    sort: args.sort ? args.sort.toString().replace(" ", "") : args.sort,
    isAscending: args.isAscending,
    searchQuery: args.searchQuery,
    username: args.username,
    shortname: args.shortname,
    context: args.context ? args.context.type.toString() : null,
    contextItemId: args.context ? args.context.itemId : null,
    startRange: args.startDate ? args.startDate.toISOString() : null,
    endRange: args.endDate ? args.endDate.toISOString() : null,
    tags: !!args.tags && args.tags.length > 0 ? args.tags.join(",") : null,
    assignedUsers:
      !!args.assignedUsers && args.assignedUsers.length > 0 ? args.assignedUsers.join(",") : null,
    selectedCategories: args.selectedCategories ?? [],
    selectedApplications: args.selectedApplications ?? [],
    selectedTopics: args.selectedTopics ?? []
  };

  const stringified = queryString.stringify(queryArgs);

  url += `?${stringified}`;

  return url;
}

// Gets feedback from the server asynchronously and dispatches a FEEDBACK_SEARCH_LOADED action when complete, or a FEEDBACK_SEARCH_SET_ERRORED action if the search failed
export const getFeedbacks = (
  args: IGetFeedbacksArguments
): ThunkAction<
  void,
  IAppState,
  {},
  IFeedbackSearchLoadedAction | ISetSearchErroredAction
> => dispatch => {
  ApiRequest.fromPath(assembleGetFeedbacksUrl(args))
    .withAuthHeader()
    .get()
    .then(response => response.json())
    .then(json => {
      if (json.status === undefined) {
        // this was successful
        dispatch({
          type: FeedbackListActionType.FEEDBACK_SEARCH_LOADED,
          payload: {
            listId: args.listId,
            loadedFeedbacks: json.results,
            fullResultsCount: json.fullResultsCount
          }
        });
      } else {
        // this failed
        dispatch({
          type: FeedbackListActionType.FEEDBACK_SEARCH_SET_ERRORED,
          payload: { listId: args.listId, errorMessage: json.title, errorCode: json.status }
        });
      }
    });
};

export const loadTransitionsIntoList = (
  args: ILoadTransitionsIntoListArguments
): ThunkAction<
  void,
  IAppState,
  {},
  IFeedbackTransitionsLoadedAction | ISetSearchErroredAction
> => dispatch => {
  ApiRequest.fromPath(endpoints.feedback.getAllTransitions())
    .withAuthHeader()
    .withJson({
      GetForFeedbackCodes: args.feedbackCodes
    })
    .post()
    .then(response => response.json())
    .then(json => {
      if (json.status === undefined) {
        // this was successful
        dispatch({
          type: FeedbackListActionType.FEEDBACK_SEARCH_TRANSITIONS_LOADED,
          payload: {
            listId: args.listId,
            loadedTransitions: json.payload.transitionsDict
          }
        });
      } else {
        // this failed
        dispatch({
          type: FeedbackListActionType.FEEDBACK_SEARCH_SET_ERRORED,
          payload: { listId: args.listId, errorMessage: json.title, errorCode: json.status }
        });
      }
    });
};

// Dispatches a FEEDBACK_SEARCH_UNLOAD action immediately
export const unloadSearch = (args: IUnloadSearchArguments): IFeedbackSearchUnloadAction => ({
  type: FeedbackListActionType.FEEDBACK_SEARCH_UNLOAD,
  payload: { ...args }
});

// Dispatches a FEEDBACK_SEARCH_SET_LOADING action immediately
export const setLoading = (args: ISetSearchLoadingArguments): ISetSearchLoadingAction => ({
  type: FeedbackListActionType.FEEDBACK_SEARCH_SET_LOADING,
  payload: { ...args }
});

export const setTransitionsLoading = (
  args: ISetSearchTransitionsLoadingArguments
): ISetSearchTransitionsLoadingAction => ({
  type: FeedbackListActionType.FEEDBACK_SEARCH_SET_TRANSITIONS_LOADING,
  payload: { ...args }
});

export const setErrored = (args: ISetSearchErroredArguments): ISetSearchErroredAction => ({
  type: FeedbackListActionType.FEEDBACK_SEARCH_SET_ERRORED,
  payload: { ...args }
});

export const updateListItem = (args: IUpdateListItemArguments): IFeedbackUpdateListItemAction => ({
  type: FeedbackListActionType.FEEDBACK_UPDATE_LIST_ITEM,
  payload: { ...args }
});

export const setSavedFilters = (
  args: ISetSearchSavedFiltersArguments
): ISetSearchSavedFiltersAction => ({
  type: FeedbackListActionType.FEEDBACK_SEARCH_SET_SAVED_FILTERS,
  payload: { ...args }
});
